import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Alert,
  Table,
  Badge,
  Loader,
  ActionIcon,
  Tabs,
  Progress
} from '@mantine/core';
import { IconArrowLeft, IconRefresh, IconEye, IconChecks, IconStack3, IconBan } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { listRuns } from '../api_scripts/runsClient';
import { listBatches, cancelBatch } from '../api_scripts/batchesClient';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';

const CANCELLABLE_STATUSES = new Set(['QUEUED', 'RUNNING']);

const STATUS_COLORS = {
  PENDING: 'gray',
  DIALING: 'blue',
  IN_PROGRESS: 'blue',
  COMPLETED: 'green',
  FAILED: 'red',
  ERROR: 'orange'
};

function RunHistory({ onBack }) {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('batches');
  const backendConfigured = isSitBackendConfigured();

  const reload = useCallback(async () => {
    if (!backendConfigured) return;
    setLoading(true);
    setError(null);
    try {
      const [r, b] = await Promise.all([listRuns(), listBatches().catch(() => [])]);
      setRuns(r);
      setBatches(b);
    } catch (err) {
      setError(err?.response?.data?.message ?? err.message ?? 'Failed to load runs.');
    } finally {
      setLoading(false);
    }
  }, [backendConfigured]);

  useEffect(() => {
    reload();
  }, [reload]);

  const confirmCancel = useCallback(
    async (batch) => {
      const msg =
        `Cancel batch ${batch.batchId} (${batch.label || 'unlabeled'})?\n\n` +
        `Any plan currently dialing finishes. Remaining queued plans skip ` +
        `without dialing and are marked CANCELLED.`;
      if (!window.confirm(msg)) return;
      try {
        await cancelBatch(batch.batchId);
        await reload();
      } catch (err) {
        setError(err?.response?.data?.message ?? err.message ?? 'Cancel failed.');
      }
    },
    [reload]
  );

  return (
    <Container size="lg" py="lg">
      <Group justify="space-between" mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back to SIT Home
        </Button>
        <ActionIcon variant="light" onClick={reload} title="Refresh" disabled={!backendConfigured}>
          <IconRefresh size={16} />
        </ActionIcon>
      </Group>

      <Stack gap="md">
        <Title order={2}>Run History</Title>

        {!backendConfigured && (
          <Alert color="yellow">
            The SIT backend URL is not configured — no runs to list.
          </Alert>
        )}
        {error && <Alert color="red">{error}</Alert>}

        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab value="batches" leftSection={<IconStack3 size={14} />}>
              Batches {batches.length > 0 && <Badge ml="xs" size="xs" variant="light">{batches.length}</Badge>}
            </Tabs.Tab>
            <Tabs.Tab value="runs" leftSection={<IconChecks size={14} />}>
              Individual runs {runs.length > 0 && <Badge ml="xs" size="xs" variant="light">{runs.length}</Badge>}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="batches" pt="md">
            <Card withBorder padding="lg" radius="md">
              {loading ? (
                <Group justify="center" py="xl"><Loader /></Group>
              ) : batches.length === 0 ? (
                <Stack gap="xs">
                  <Text size="sm" fw={500}>No batches yet.</Text>
                  <Text size="xs" c="dimmed">
                    Approve plans on the Verify Plans screen and click "Send N to Run (batch)" —
                    a batch will be queued server-side and show up here even if you close the browser.
                  </Text>
                </Stack>
              ) : (
                <Table verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Batch ID</Table.Th>
                      <Table.Th>Label</Table.Th>
                      <Table.Th style={{ width: 120 }}>Status</Table.Th>
                      <Table.Th style={{ width: 200 }}>Progress</Table.Th>
                      <Table.Th>Started</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {batches.map((b) => {
                      const pct = b.totalCount > 0 ? (b.doneCount / b.totalCount) * 100 : 0;
                      const color =
                        b.status === 'COMPLETED' ? 'green'
                        : b.status === 'FAILED' ? 'red'
                        : b.status === 'CANCELLED' ? 'gray'
                        : 'blue';
                      return (
                        <Table.Tr key={b.batchId}>
                          <Table.Td><code>{b.batchId}</code></Table.Td>
                          <Table.Td><Text size="sm" lineClamp={1}>{b.label || '—'}</Text></Table.Td>
                          <Table.Td><Badge color={color} variant="light">{b.status}</Badge></Table.Td>
                          <Table.Td>
                            <Stack gap={2}>
                              <Progress value={pct} size="sm" color={color} animated={b.status === 'RUNNING' || b.status === 'QUEUED'} />
                              <Text size="xs" c="dimmed">{b.doneCount} / {b.totalCount}</Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>{b.createdAt ? new Date(b.createdAt).toLocaleString() : '—'}</Table.Td>
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              <ActionIcon variant="subtle" onClick={() => navigate(`/history/batches/${b.batchId}`)} title="View batch">
                                <IconEye size={16} />
                              </ActionIcon>
                              {CANCELLABLE_STATUSES.has(b.status) && (
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={() => confirmCancel(b)}
                                  title="Cancel batch"
                                >
                                  <IconBan size={16} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="runs" pt="md">
            <Card withBorder padding="lg" radius="md">
              {loading ? (
                <Group justify="center" py="xl"><Loader /></Group>
              ) : runs.length === 0 ? (
                <Text size="sm" color="dimmed">No runs yet.</Text>
              ) : (
                <Table verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Run ID</Table.Th>
                      <Table.Th>Plan</Table.Th>
                      <Table.Th>CCaaS</Table.Th>
                      <Table.Th>Destination</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Started</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {runs.map((r) => (
                      <Table.Tr key={r.testRunId}>
                        <Table.Td><code>{r.testRunId}</code></Table.Td>
                        <Table.Td>{r.planId}</Table.Td>
                        <Table.Td>{r.ccaas}</Table.Td>
                        <Table.Td><code>{r.destination}</code></Table.Td>
                        <Table.Td><Badge color={STATUS_COLORS[r.status] ?? 'gray'}>{r.status}</Badge></Table.Td>
                        <Table.Td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</Table.Td>
                        <Table.Td>
                          <ActionIcon variant="subtle" onClick={() => navigate(`/history/${r.testRunId}`)}>
                            <IconEye size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default RunHistory;
